import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  deleteDoc
} from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import confetti from 'canvas-confetti';

// Unified project stages for the complete workflow
const PROJECT_STAGES = {
  // Pipeline stages
  INCOMING: 'incoming',
  QUALIFYING: 'qualifying', 
  QUALIFIED: 'qualified',
  PROPOSAL: 'proposal',
  NEGOTIATION: 'negotiation',
  // Project stages
  ACTIVE: 'active',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
};

// Qualification stages for Kanban view
const QUALIFICATION_STAGES = [
  { 
    id: 'qualifying', 
    name: 'Initial Contact', 
    color: '#3B82F6',
    description: 'Gathering requirements'
  },
  { 
    id: 'qualified', 
    name: 'Budget Confirmed', 
    color: '#10B981',
    description: 'Ready for proposal'
  },
  { 
    id: 'proposal', 
    name: 'Proposal Sent', 
    color: '#F59E0B',
    description: 'Awaiting decision'
  },
  { 
    id: 'negotiation', 
    name: 'Negotiating', 
    color: '#8B5CF6',
    description: 'Finalizing terms'
  }
];

// Get stage info for display
const getStageInfo = (stage) => {
  const stageMap = {
    [PROJECT_STAGES.INCOMING]: { name: 'New', color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300' },
    [PROJECT_STAGES.QUALIFYING]: { name: 'Qualifying', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
    [PROJECT_STAGES.QUALIFIED]: { name: 'Qualified', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
    [PROJECT_STAGES.PROPOSAL]: { name: 'Proposal', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
    [PROJECT_STAGES.NEGOTIATION]: { name: 'Negotiating', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' }
  };
  return stageMap[stage] || stageMap[PROJECT_STAGES.INCOMING];
};

// Incoming Request Card Component
const IncomingRequestCard = ({ request, onViewDetails, onQuickQualify, onDismiss, onStageChange }) => {
  const [isHovered, setIsHovered] = useState(false);
  const daysSince = Math.floor((Date.now() - request.createdAt?.toDate()) / (1000 * 60 * 60 * 24));
  const isUrgent = daysSince > 2;
  const stageInfo = getStageInfo(request.stage);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`border border-gray-200 dark:border-[#2A2A2A] rounded-xl sm:rounded-2xl p-4 sm:p-5 transition-all duration-200 ${
        isUrgent 
          ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10' 
          : 'border-gray-200 dark:border-[#2A2A2A] hover:border-brand/30'
      }`}
    >
      {/* Urgent indicator */}
      {isUrgent && (
        <div className="flex items-center gap-2 mb-3 text-red-600 dark:text-red-400">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span className="text-xs font-semibold">URGENT - {daysSince} days old</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3 flex-1">
          {/* Avatar */}
          <div className="w-10 sm:w-12 h-10 sm:h-12 rounded-full bg-gradient-to-br from-brand to-[#FF9D5C] flex items-center justify-center text-base sm:text-lg font-bold text-black flex-shrink-0">
            {request.clientName?.charAt(0)?.toUpperCase() || '?'}
          </div>
          
          {/* Info */}
          <div className="flex-1 min-w-0">
            <h4 className="font-bold text-gray-900 dark:text-white text-base sm:text-lg leading-tight mb-1">
              {request.clientName || 'Unknown Client'}
            </h4>
            <p className="text-sm text-gray-500 dark:text-[#666666] mb-1">
              {request.clientEmail}
            </p>
            <div className="flex items-center gap-2 mt-1">
              {request.projectType && (
                <p className="text-sm font-medium text-gray-700 dark:text-[#888888]">
                  {request.projectType}
                </p>
              )}
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${stageInfo.color}`}>
                {stageInfo.name}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className={`flex items-center gap-2 transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
          <button
            onClick={() => onDismiss(request)}
            className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            title="Dismiss"
          >
            <svg className="w-4 h-4 text-red-400 hover:text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Quick info */}
      <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
        {request.budget && (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
            💰 {request.budget}
          </span>
        )}
        {request.timeline && (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
            ⏱️ {request.timeline}
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          onClick={() => onViewDetails(request)}
          className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-100 dark:bg-[#2A2A2A] hover:bg-gray-200 dark:hover:bg-[#333333] text-gray-700 dark:text-[#CCCCCC] font-medium rounded-lg sm:rounded-xl transition-colors text-sm"
        >
          View Details
        </button>
        
        {/* Stage-specific actions */}
        {request.stage === PROJECT_STAGES.INCOMING && (
          <button
            onClick={() => onStageChange(request, PROJECT_STAGES.QUALIFYING)}
            className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 bg-brand hover:bg-brand-hover text-white font-semibold rounded-lg sm:rounded-xl transition-colors text-sm"
          >
            Start Qualifying
          </button>
        )}
        
        {request.stage === PROJECT_STAGES.QUALIFYING && (
          <button
            onClick={() => onStageChange(request, PROJECT_STAGES.QUALIFIED)}
            className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg sm:rounded-xl transition-colors text-sm"
          >
            Mark Qualified
          </button>
        )}
        
        {request.stage === PROJECT_STAGES.QUALIFIED && (
          <button
            onClick={() => onStageChange(request, PROJECT_STAGES.PROPOSAL)}
            className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-lg sm:rounded-xl transition-colors text-sm"
          >
            Send Proposal
          </button>
        )}
        
        {request.stage === PROJECT_STAGES.PROPOSAL && (
          <button
            onClick={() => onStageChange(request, PROJECT_STAGES.NEGOTIATION)}
            className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg sm:rounded-xl transition-colors text-sm"
          >
            Start Negotiation
          </button>
        )}
        
        {request.stage === PROJECT_STAGES.NEGOTIATION && (
          <button
            onClick={() => onViewDetails(request)}
            className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg sm:rounded-xl transition-colors text-sm"
          >
            Convert to Project
          </button>
        )}
      </div>
    </motion.div>
  );
};

// Qualified Lead Card in Kanban
const QualifiedLeadCard = ({ lead, onViewDetails, onConvertToProject, onDragStart }) => {
  const score = Math.min(100, (lead.score || 0));
  
  return (
    <motion.div
      layoutId={lead.id}
      draggable
      onDragStart={(e) => onDragStart(e, lead)}
      whileHover={{ scale: 1.02 }}
      whileDrag={{ scale: 1.05, rotate: 1 }}
      className="border border-gray-200 dark:border-[#2A2A2A] rounded-xl p-4 cursor-move hover:shadow-md transition-all duration-200"
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand to-[#FF9D5C] flex items-center justify-center text-xs font-bold text-black">
          {lead.clientName?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <h5 className="font-semibold text-gray-900 dark:text-white text-sm leading-tight">
            {lead.clientName}
          </h5>
          <p className="text-xs text-gray-500 dark:text-[#666666]">
            {lead.projectType}
          </p>
        </div>
      </div>

      {/* Score bar */}
      {score > 0 && (
        <div className="mb-3">
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs text-gray-500 dark:text-[#666666]">Score</span>
            <span className="text-xs font-bold text-gray-700 dark:text-[#CCCCCC]">{score}%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-[#2A2A2A] rounded-full h-1.5">
            <div 
              className="h-1.5 rounded-full bg-brand transition-all duration-500"
              style={{ width: `${score}%` }}
            />
          </div>
        </div>
      )}

      {/* Tags */}
      {lead.budget && (
        <div className="mb-3">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
            ${lead.estimatedValue?.toLocaleString() || lead.budget}
          </span>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={() => onViewDetails(lead)}
          className="flex-1 px-3 py-1.5 text-xs bg-gray-100 dark:bg-[#2A2A2A] text-gray-600 dark:text-[#CCCCCC] rounded-lg hover:bg-gray-200 dark:hover:bg-[#333333] transition-colors"
        >
          Details
        </button>
        {lead.stage === 'negotiation' && (
          <button
            onClick={() => onConvertToProject(lead)}
            className="flex-1 px-3 py-1.5 text-xs bg-brand text-white font-medium rounded-lg hover:bg-brand-hover transition-colors"
          >
            Convert
          </button>
        )}
      </div>
    </motion.div>
  );
};

// Project List Item
const ProjectListItem = ({ project, onClick, isActive = true }) => {
  const completionPercentage = project.tasks?.length > 0 
    ? Math.round((project.tasks.filter(task => task.status === 'complete').length / project.tasks.length) * 100)
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      onClick={() => onClick(project)}
      className="flex items-center gap-4 p-4 border border-gray-200 dark:border-[#2A2A2A] rounded-xl hover:border-brand/30 cursor-pointer transition-all duration-200 hover:shadow-sm"
    >
      {/* Avatar */}
      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-brand to-[#FF9D5C] flex items-center justify-center text-sm font-bold text-black flex-shrink-0">
        {project.projectName?.charAt(0)?.toUpperCase() || project.clientName?.charAt(0)?.toUpperCase() || 'P'}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <h5 className="font-semibold text-gray-900 dark:text-white text-sm leading-tight mb-1">
          {project.projectName || 'Untitled Project'}
        </h5>
        <p className="text-xs text-gray-500 dark:text-[#666666]">
          {project.clientName || project.clientEmail}
        </p>
        
        {/* Progress bar for active projects */}
        {isActive && project.tasks?.length > 0 && (
          <div className="mt-2">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs text-gray-500 dark:text-[#666666]">Progress</span>
              <span className="text-xs font-medium text-gray-700 dark:text-[#CCCCCC]">{completionPercentage}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-[#2A2A2A] rounded-full h-1">
              <div 
                className="h-1 rounded-full bg-brand transition-all duration-500"
                style={{ width: `${completionPercentage}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Status indicator */}
      <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
        isActive ? 'bg-green-400' : 'bg-gray-400'
      }`} />
    </motion.div>
  );
};

// Qualification Stage Column
const QualificationStageColumn = ({ stage, leads, onDrop, onDragOver, onViewDetails, onConvertToProject }) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
    onDragOver(e);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    setIsDragOver(false);
    onDrop(e, stage.id);
  };

  return (
    <div 
      className="flex-1 min-w-[160px] sm:min-w-[180px] max-w-[280px]"
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <div className={`h-full rounded-xl sm:rounded-2xl border-2 border-dashed transition-all duration-200 ${
        isDragOver ? 'border-brand bg-brand/5' : 'border-gray-200 dark:border-[#2A2A2A]'
      }`}>
        {/* Stage Header */}
        <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-[#2A2A2A]">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <h4 className="font-bold text-gray-900 dark:text-white text-xs sm:text-sm truncate">
                {stage.name}
              </h4>
              <p className="text-xs text-gray-500 dark:text-[#666666] truncate">
                {stage.description}
              </p>
            </div>
            <span className="px-2 py-1 border border-gray-200 dark:border-[#2A2A2A] rounded-lg text-xs font-bold text-gray-900 dark:text-white flex-shrink-0 ml-2">
              {leads.length}
            </span>
          </div>
        </div>

        {/* Leads */}
        <div className="p-2 sm:p-3 space-y-2 sm:space-y-3 max-h-[350px] xl:max-h-[400px] overflow-y-auto">
          <AnimatePresence>
            {leads.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-[#2A2A2A] flex items-center justify-center mx-auto mb-2">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                <p className="text-xs text-gray-500 dark:text-[#666666]">No leads</p>
              </div>
            ) : (
              leads.map((lead) => (
                <QualifiedLeadCard
                  key={lead.id}
                  lead={lead}
                  onViewDetails={onViewDetails}
                  onConvertToProject={onConvertToProject}
                  onDragStart={(e, lead) => e.dataTransfer.setData('leadId', lead.id)}
                />
              ))
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

// Lead Details Modal
const LeadDetailsModal = ({ lead, isOpen, onClose, onUpdateLead, onConvertToProject }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (lead) {
      setFormData({
        clientName: lead.clientName || '',
        clientEmail: lead.clientEmail || '',
        projectType: lead.projectType || '',
        budget: lead.budget || '',
        timeline: lead.timeline || '',
        notes: lead.notes || ''
      });
    }
  }, [lead]);

  const handleSave = async () => {
    try {
      await onUpdateLead(lead.id, formData);
      setIsEditing(false);
      toast.success('Lead updated successfully');
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  if (!isOpen || !lead) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="border border-gray-200 dark:border-[#2A2A2A] rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="px-8 py-6 border-b border-gray-200 dark:border-[#2A2A2A]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand to-[#FF9D5C] flex items-center justify-center text-lg font-bold text-black">
                {lead.clientName?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {isEditing ? 'Edit Lead' : lead.clientName}
                </h2>
                <p className="text-sm text-gray-500 dark:text-[#666666]">
                  {lead.projectType} • Stage: {lead.stage}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 bg-gray-100 dark:bg-[#2A2A2A] text-gray-700 dark:text-[#CCCCCC] rounded-xl hover:bg-gray-200 dark:hover:bg-[#333333] transition-colors"
                >
                  Edit
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 bg-gray-100 dark:bg-[#2A2A2A] text-gray-700 dark:text-[#CCCCCC] rounded-xl hover:bg-gray-200 dark:hover:bg-[#333333] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-brand text-white font-semibold rounded-xl hover:bg-brand-hover transition-colors"
                  >
                    Save
                  </button>
                </div>
              )}
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-[#2A2A2A] rounded-xl transition-colors"
              >
                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 max-h-[60vh] overflow-y-auto">
          {isEditing ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Client Name
                  </label>
                  <input
                    type="text"
                    value={formData.clientName}
                    onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-black rounded-xl border border-gray-200 dark:border-[#2A2A2A] text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.clientEmail}
                    onChange={(e) => setFormData({ ...formData, clientEmail: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-black rounded-xl border border-gray-200 dark:border-[#2A2A2A] text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-black rounded-xl border border-gray-200 dark:border-[#2A2A2A] text-gray-900 dark:text-white resize-none"
                  placeholder="Add notes about this lead..."
                />
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Lead info */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Contact Information</h4>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 dark:text-[#777777]">
                      <span className="font-medium">Email:</span> {lead.clientEmail}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-[#777777]">
                      <span className="font-medium">Project:</span> {lead.projectType || 'Not specified'}
                    </p>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Project Details</h4>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 dark:text-[#777777]">
                      <span className="font-medium">Budget:</span> {lead.budget || 'Not specified'}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-[#777777]">
                      <span className="font-medium">Timeline:</span> {lead.timeline || 'Not specified'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {lead.notes && (
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Notes</h4>
                  <div className="p-4 bg-gray-50 dark:bg-[#2A2A2A] rounded-xl">
                    <p className="text-sm text-gray-700 dark:text-[#CCCCCC] whitespace-pre-wrap">
                      {lead.notes}
                    </p>
                  </div>
                </div>
              )}

              {/* Actions */}
              {lead.stage === 'negotiation' && (
                <div className="pt-4 border-t border-gray-200 dark:border-[#2A2A2A]">
                  <button
                    onClick={() => onConvertToProject(lead)}
                    className="w-full px-6 py-3 bg-brand text-white font-semibold rounded-xl hover:bg-brand-hover transition-colors"
                  >
                    Convert to Project
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Unified Projects Dashboard
export default function UnifiedProjectsDashboard() {
  const navigate = useNavigate();
  const auth = getAuth();
  const db = getFirestore();
  
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'kanban'

  // Quick Add Lead Form Component
  const QuickAddLeadForm = ({ isOpen, onClose, onAdd }) => {
    const [formData, setFormData] = useState({
      clientName: '',
      clientEmail: '',
      projectType: '',
      budget: '',
      timeline: '',
      referralSource: ''
    });
    
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!formData.clientName || !formData.clientEmail) {
        toast.error('Name and email are required');
        return;
      }
      
      setIsSubmitting(true);
      try {
        await onAdd(formData);
        setFormData({
          clientName: '',
          clientEmail: '',
          projectType: '',
          budget: '',
          timeline: '',
          referralSource: ''
        });
        onClose();
      } catch (error) {
        console.error('Error adding lead:', error);
        toast.error('Failed to add lead');
      } finally {
        setIsSubmitting(false);
      }
    };

    if (!isOpen) return null;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="border border-gray-200 dark:border-[#2A2A2A] rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="px-8 py-6 border-b border-gray-200 dark:border-[#2A2A2A]">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Add New Lead</h2>
                <p className="text-sm text-gray-500 dark:text-[#666666] mt-1">
                  Create a new incoming request
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-[#2A2A2A] rounded-xl transition-colors"
              >
                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-6 max-h-[60vh] overflow-y-auto">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Client Name *
                </label>
                <input
                  type="text"
                  value={formData.clientName}
                  onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-black rounded-xl border border-gray-200 dark:border-[#2A2A2A] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:ring-2 focus:ring-brand/20 focus:border-brand transition-colors"
                  placeholder="Enter client's full name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.clientEmail}
                  onChange={(e) => setFormData({ ...formData, clientEmail: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-black rounded-xl border border-gray-200 dark:border-[#2A2A2A] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:ring-2 focus:ring-brand/20 focus:border-brand transition-colors"
                  placeholder="client@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Project Type
                </label>
                <input
                  type="text"
                  value={formData.projectType}
                  onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-black rounded-xl border border-gray-200 dark:border-[#2A2A2A] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:ring-2 focus:ring-brand/20 focus:border-brand transition-colors"
                  placeholder="Wedding, Corporate Event, etc."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Budget Range
                  </label>
                  <select
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-black rounded-xl border border-gray-200 dark:border-[#2A2A2A] text-gray-900 dark:text-white focus:ring-2 focus:ring-brand/20 focus:border-brand transition-colors"
                  >
                    <option value="">Select budget</option>
                    <option value="Under $1,000">Under $1,000</option>
                    <option value="$1,000 - $5,000">$1,000 - $5,000</option>
                    <option value="$5,000 - $10,000">$5,000 - $10,000</option>
                    <option value="$10,000 - $25,000">$10,000 - $25,000</option>
                    <option value="Over $25,000">Over $25,000</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Timeline
                  </label>
                  <select
                    value={formData.timeline}
                    onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-black rounded-xl border border-gray-200 dark:border-[#2A2A2A] text-gray-900 dark:text-white focus:ring-2 focus:ring-brand/20 focus:border-brand transition-colors"
                  >
                    <option value="">Select timeline</option>
                    <option value="ASAP">ASAP</option>
                    <option value="Within 1 month">Within 1 month</option>
                    <option value="1-3 months">1-3 months</option>
                    <option value="3-6 months">3-6 months</option>
                    <option value="Flexible">Flexible</option>
                  </select>
                </div>
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="px-8 py-6 border-t border-gray-200 dark:border-[#2A2A2A] flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-sm font-semibold text-gray-700 dark:text-[#666666] bg-gray-100 dark:bg-[#2A2A2A] hover:bg-gray-200 dark:hover:bg-[#333333] rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !formData.clientName || !formData.clientEmail}
              className={`px-6 py-3 text-sm font-semibold rounded-xl transition-colors ${
                isSubmitting || !formData.clientName || !formData.clientEmail
                  ? 'bg-gray-200 dark:bg-[#2A2A2A] text-gray-500 cursor-not-allowed'
                  : 'bg-brand hover:bg-brand-hover text-white shadow-sm'
              }`}
            >
              {isSubmitting ? 'Adding Lead...' : 'Add Lead'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  // Load data from Firestore
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/login');
        return;
      }

      // Load leads
      const leadsRef = collection(db, 'users', user.uid, 'leads');
      const leadsUnsubscribe = onSnapshot(leadsRef, (snapshot) => {
        const leadsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })).filter(lead => lead.archived !== true);
        setLeads(leadsData);
      });

      // Load projects
      const projectsRef = collection(db, 'projects');
      const projectsQuery = query(projectsRef, where('userId', '==', user.uid));
      const projectsUnsubscribe = onSnapshot(projectsQuery, (snapshot) => {
        const projectsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setProjects(projectsData);
        setLoading(false);
      });

      return () => {
        leadsUnsubscribe();
        projectsUnsubscribe();
      };
    });

    return () => unsubscribeAuth();
  }, [auth, db, navigate]);

  // Categorize leads and projects - now all pipeline leads go in incoming requests
  const allPipelineLeads = leads.filter(lead => 
    [PROJECT_STAGES.INCOMING, PROJECT_STAGES.QUALIFYING, PROJECT_STAGES.QUALIFIED, PROJECT_STAGES.PROPOSAL, PROJECT_STAGES.NEGOTIATION]
    .includes(lead.stage) || !lead.stage
  );
  const qualifiedLeads = leads.filter(lead => 
    [PROJECT_STAGES.QUALIFYING, PROJECT_STAGES.QUALIFIED, PROJECT_STAGES.PROPOSAL, PROJECT_STAGES.NEGOTIATION]
    .includes(lead.stage)
  );
  const activeProjects = projects.filter(project => 
    project.status === PROJECT_STAGES.ACTIVE || !project.status
  );
  const completedProjects = projects.filter(project => 
    project.status === PROJECT_STAGES.COMPLETED
  );

  // Group qualified leads by stage
  const qualifiedLeadsByStage = QUALIFICATION_STAGES.reduce((acc, stage) => {
    acc[stage.id] = qualifiedLeads.filter(lead => lead.stage === stage.id);
    return acc;
  }, {});

  // Handle drag and drop for qualification
  const handleQualificationDrop = async (e, newStage) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData('leadId');
    const lead = leads.find(l => l.id === leadId);
    
    if (!lead || lead.stage === newStage) return;

    const user = auth.currentUser;
    const leadRef = doc(db, 'users', user.uid, 'leads', leadId);
    
    try {
      await updateDoc(leadRef, {
        stage: newStage,
        lastUpdated: serverTimestamp()
      });
      toast.success(`Lead moved to ${newStage} stage`);
    } catch (error) {
      console.error('Error updating lead stage:', error);
      toast.error('Failed to update lead stage');
    }
  };

  // Convert lead to project
  const handleConvertToProject = async (lead) => {
    try {
      const user = auth.currentUser;
      
      // Create new project
      const projectData = {
        projectName: `${lead.projectType || 'Project'} - ${lead.clientName}`,
        clientName: lead.clientName,
        clientEmail: lead.clientEmail,
        projectType: lead.projectType,
        budget: lead.estimatedValue,
        status: PROJECT_STAGES.ACTIVE,
        userId: user.uid,
        createdAt: serverTimestamp(),
        tasks: [],
        collaborators: [],
        sourceLeadId: lead.id
      };

      await addDoc(collection(db, 'projects'), projectData);
      
      // Update lead status to converted
      const leadRef = doc(db, 'users', user.uid, 'leads', lead.id);
      await updateDoc(leadRef, {
        stage: 'converted',
        convertedAt: serverTimestamp(),
        archived: true
      });

      // Celebration
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });

      toast.success('Lead converted to project successfully!');
      setShowLeadModal(false);
    } catch (error) {
      console.error('Error converting lead to project:', error);
      toast.error('Failed to convert lead to project');
    }
  };

  // Handle stage changes
  const handleStageChange = async (request, newStage) => {
    const user = auth.currentUser;
    const leadRef = doc(db, 'users', user.uid, 'leads', request.id);
    
    try {
      await updateDoc(leadRef, {
        stage: newStage,
        lastUpdated: serverTimestamp()
      });
      const stageNames = {
        [PROJECT_STAGES.QUALIFYING]: 'qualification',
        [PROJECT_STAGES.QUALIFIED]: 'qualified',
        [PROJECT_STAGES.PROPOSAL]: 'proposal',
        [PROJECT_STAGES.NEGOTIATION]: 'negotiation'
      };
      toast.success(`Lead moved to ${stageNames[newStage]} stage`);
    } catch (error) {
      console.error('Error updating lead stage:', error);
      toast.error('Failed to update lead stage');
    }
  };

  const handleDismissRequest = async (request) => {
    if (!window.confirm(`Dismiss request from ${request.clientName}?`)) return;
    
    const user = auth.currentUser;
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'leads', request.id));
      toast.success('Request dismissed');
    } catch (error) {
      console.error('Error dismissing request:', error);
      toast.error('Failed to dismiss request');
    }
  };

  // Update lead
  const handleUpdateLead = async (leadId, updatedData) => {
    const user = auth.currentUser;
    const leadRef = doc(db, 'users', user.uid, 'leads', leadId);
    
    try {
      await updateDoc(leadRef, {
        ...updatedData,
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating lead:', error);
      throw error;
    }
  };

  // View lead details
  const handleViewLeadDetails = (lead) => {
    setSelectedLead(lead);
    setShowLeadModal(true);
  };

  // View project details
  const handleViewProjectDetails = (project) => {
    navigate(`/app/projects/${project.id}`);
  };

  // Add new lead
  const handleAddLead = async (formData) => {
    const user = auth.currentUser;
    if (!user) return;

    const leadData = {
      ...formData,
      stage: PROJECT_STAGES.INCOMING,
      touchpointCount: 0,
      touchpoints: [],
      createdAt: serverTimestamp(),
      lastUpdated: serverTimestamp(),
      lastContact: serverTimestamp(),
      userId: user.uid,
      archived: false
    };

    // Calculate estimated value based on budget
    if (formData.budget) {
      const budgetMap = {
        'Under $1,000': 500,
        '$1,000 - $5,000': 3000,
        '$5,000 - $10,000': 7500,
        '$10,000 - $25,000': 17500,
        'Over $25,000': 30000
      };
      leadData.estimatedValue = budgetMap[formData.budget] || 0;
    }

    try {
      await addDoc(collection(db, 'users', user.uid, 'leads'), leadData);
      toast.success('Lead added successfully!');
    } catch (error) {
      console.error('Error adding lead:', error);
      toast.error('Failed to add lead');
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream dark:bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-[#2A2A2A]">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 lg:gap-0">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                Projects Dashboard
              </h1>
              <p className="text-sm sm:text-base lg:text-lg text-gray-600 dark:text-[#777777]">
                From incoming leads to completed projects
              </p>
            </div>
            
            {/* Quick stats - responsive */}
            <div className="hidden lg:flex items-center gap-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{allPipelineLeads.length}</p>
                <p className="text-sm text-gray-500 dark:text-[#666666]">Pipeline Leads</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{activeProjects.length}</p>
                <p className="text-sm text-gray-500 dark:text-[#666666]">Active Projects</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">{completedProjects.length}</p>
                <p className="text-sm text-gray-500 dark:text-[#666666]">Completed</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Responsive Dashboard */}
      <div className="w-full px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6">
        <div className="max-w-[1400px] mx-auto">
          {/* Desktop Layout (XL and above) */}
          <div className="hidden xl:block">
            <div className="grid grid-cols-3 gap-6">
              {/* Left Side - Pipeline Leads */}
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      Pipeline Leads
                      {allPipelineLeads.length > 0 && (
                        <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
                          {allPipelineLeads.length}
                        </span>
                      )}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-[#777777]">All leads from initial contact to ready to convert</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setViewMode(viewMode === 'list' ? 'kanban' : 'list')}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-[#2A2A2A] rounded-lg transition-colors"
                      title={`Switch to ${viewMode === 'list' ? 'kanban' : 'list'} view`}
                    >
                      {viewMode === 'list' ? (
                        <svg className="w-4 h-4 text-gray-600 dark:text-[#CCCCCC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h2a2 2 0 002-2z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-gray-600 dark:text-[#CCCCCC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={() => setShowQuickAdd(true)}
                      className="px-3 py-2 bg-brand text-white font-semibold rounded-xl hover:bg-brand-hover transition-colors text-sm"
                    >
                      Add Lead
                    </button>
                  </div>
                </div>
                
                <div className="border border-gray-200 dark:border-[#2A2A2A] rounded-2xl p-4" style={{height: 'calc(100vh - 320px)', minHeight: '500px'}}>
                  {allPipelineLeads.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <div className="w-12 h-12 rounded-full border border-gray-200 dark:border-[#2A2A2A] flex items-center justify-center mb-3">
                        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                      </div>
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-1">No pipeline leads</h3>
                      <p className="text-xs text-gray-500 dark:text-[#666666]">Add leads to start building your pipeline</p>
                    </div>
                  ) : viewMode === 'list' ? (
                    <div className="h-full overflow-y-auto">
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                        {allPipelineLeads.map((request) => (
                          <IncomingRequestCard
                            key={request.id}
                            request={request}
                            onViewDetails={handleViewLeadDetails}
                            onStageChange={handleStageChange}
                            onDismiss={handleDismissRequest}
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-3 h-full overflow-x-auto">
                      {QUALIFICATION_STAGES.map((stage) => (
                        <QualificationStageColumn
                          key={stage.id}
                          stage={stage}
                          leads={qualifiedLeadsByStage[stage.id] || []}
                          onDrop={handleQualificationDrop}
                          onDragOver={(e) => e.preventDefault()}
                          onViewDetails={handleViewLeadDetails}
                          onConvertToProject={handleConvertToProject}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Side - Projects */}
              <div className="col-span-1 space-y-6">
                {/* Active Projects */}
                <div>
                  <div className="mb-4">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                      Active Projects
                      {activeProjects.length > 0 && (
                        <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-green-600 dark:text-green-400 border border-green-200 dark:border-green-800">
                          {activeProjects.length}
                        </span>
                      )}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-[#777777]">In progress</p>
                  </div>
                  
                  <div className="border border-gray-200 dark:border-[#2A2A2A] rounded-2xl p-4" style={{height: 'calc((100vh - 380px) / 2)', minHeight: '200px'}}>
                    <div className="space-y-3 h-full overflow-y-auto">
                      {activeProjects.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center">
                          <div className="w-10 h-10 rounded-full border border-gray-200 dark:border-[#2A2A2A] flex items-center justify-center mb-2">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-[#666666]">No active projects</p>
                        </div>
                      ) : (
                        activeProjects.map((project) => (
                          <ProjectListItem
                            key={project.id}
                            project={project}
                            onClick={handleViewProjectDetails}
                            isActive={true}
                          />
                        ))
                      )}
                    </div>
                  </div>
                </div>

                {/* Completed Projects */}
                <div>
                  <div className="mb-4">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                      Completed Projects
                      {completedProjects.length > 0 && (
                        <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-800">
                          {completedProjects.length}
                        </span>
                      )}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-[#777777]">Successfully delivered</p>
                  </div>
                  
                  <div className="border border-gray-200 dark:border-[#2A2A2A] rounded-2xl p-4" style={{height: 'calc((100vh - 380px) / 2)', minHeight: '200px'}}>
                    <div className="space-y-3 h-full overflow-y-auto">
                      {completedProjects.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center">
                          <div className="w-10 h-10 rounded-full border border-gray-200 dark:border-[#2A2A2A] flex items-center justify-center mb-2">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                            </svg>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-[#666666]">No completed projects</p>
                        </div>
                      ) : (
                        completedProjects.map((project) => (
                          <ProjectListItem
                            key={project.id}
                            project={project}
                            onClick={handleViewProjectDetails}
                            isActive={false}
                          />
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tablet Layout (md to lg) */}
          <div className="hidden lg:block xl:hidden">
            <div className="space-y-8">
              {/* Top Row - Pipeline Leads */}
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      Pipeline Leads
                      {allPipelineLeads.length > 0 && (
                        <span className="ml-2 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                          {allPipelineLeads.length} Total
                        </span>
                      )}
                    </h2>
                    <p className="text-gray-600 dark:text-[#777777]">All leads from initial contact to ready to convert</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setViewMode(viewMode === 'list' ? 'kanban' : 'list')}
                      className="p-2 bg-gray-100 dark:bg-[#2A2A2A] hover:bg-gray-200 dark:hover:bg-[#333333] rounded-lg transition-colors"
                      title={`Switch to ${viewMode === 'list' ? 'kanban' : 'list'} view`}
                    >
                      {viewMode === 'list' ? (
                        <svg className="w-4 h-4 text-gray-600 dark:text-[#CCCCCC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h2a2 2 0 002-2z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-gray-600 dark:text-[#CCCCCC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={() => setShowQuickAdd(true)}
                      className="px-4 py-2 bg-brand text-white font-semibold rounded-xl hover:bg-brand-hover transition-colors"
                    >
                      Add Lead
                    </button>
                  </div>
                </div>
                
                <div className="border border-gray-200 dark:border-[#2A2A2A] rounded-2xl p-6 min-h-[400px]">
                  {allPipelineLeads.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <div className="w-16 h-16 border border-gray-200 dark:border-[#2A2A2A] rounded-full flex items-center justify-center mb-4">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No pipeline leads</h3>
                      <p className="text-gray-500 dark:text-[#666666]">Add leads to start building your pipeline</p>
                    </div>
                  ) : viewMode === 'list' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {allPipelineLeads.map((request) => (
                        <IncomingRequestCard
                          key={request.id}
                          request={request}
                          onViewDetails={handleViewLeadDetails}
                          onStageChange={handleStageChange}
                          onDismiss={handleDismissRequest}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="flex gap-3 overflow-x-auto pb-2">
                      {QUALIFICATION_STAGES.map((stage) => (
                        <QualificationStageColumn
                          key={stage.id}
                          stage={stage}
                          leads={qualifiedLeadsByStage[stage.id] || []}
                          onDrop={handleQualificationDrop}
                          onDragOver={(e) => e.preventDefault()}
                          onViewDetails={handleViewLeadDetails}
                          onConvertToProject={handleConvertToProject}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Projects */}
              <div>
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Projects
                  </h2>
                  <p className="text-gray-600 dark:text-[#777777]">Active and completed projects</p>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Active Projects */}
                  <div className="border border-gray-200 dark:border-[#2A2A2A] rounded-2xl p-6">
                    <h3 className="font-bold text-gray-900 dark:text-white mb-4">Active Projects ({activeProjects.length})</h3>
                    <div className="space-y-3 max-h-[200px] overflow-y-auto">
                      {activeProjects.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-[#666666] text-center py-8">No active projects</p>
                      ) : (
                        activeProjects.map((project) => (
                          <ProjectListItem
                            key={project.id}
                            project={project}
                            onClick={handleViewProjectDetails}
                            isActive={true}
                          />
                        ))
                      )}
                    </div>
                  </div>

                  {/* Completed Projects */}
                  <div className="border border-gray-200 dark:border-[#2A2A2A] rounded-2xl p-6">
                    <h3 className="font-bold text-gray-900 dark:text-white mb-4">Completed Projects ({completedProjects.length})</h3>
                    <div className="space-y-3 max-h-[200px] overflow-y-auto">
                      {completedProjects.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-[#666666] text-center py-8">No completed projects</p>
                      ) : (
                        completedProjects.map((project) => (
                          <ProjectListItem
                            key={project.id}
                            project={project}
                            onClick={handleViewProjectDetails}
                            isActive={false}
                          />
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Layout (sm and below) */}
          <div className="block lg:hidden">
            <div className="space-y-6">
              {/* Add Lead Button */}
              <div className="flex justify-end">
                <button
                  onClick={() => setShowQuickAdd(true)}
                  className="px-4 py-2 bg-brand text-white font-semibold rounded-xl hover:bg-brand-hover transition-colors"
                >
                  Add Lead
                </button>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-3 sm:gap-4">
                <div className="border border-gray-200 dark:border-[#2A2A2A] rounded-xl p-3 sm:p-4 text-center">
                  <p className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">{allPipelineLeads.length}</p>
                  <p className="text-xs text-gray-500 dark:text-[#666666]">Pipeline</p>
                </div>
                <div className="border border-gray-200 dark:border-[#2A2A2A] rounded-xl p-3 sm:p-4 text-center">
                  <p className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">{activeProjects.length}</p>
                  <p className="text-xs text-gray-500 dark:text-[#666666]">Active</p>
                </div>
                <div className="border border-gray-200 dark:border-[#2A2A2A] rounded-xl p-3 sm:p-4 text-center">
                  <p className="text-xl sm:text-2xl font-bold text-gray-600 dark:text-gray-400">{completedProjects.length}</p>
                  <p className="text-xs text-gray-500 dark:text-[#666666]">Completed</p>
                </div>
              </div>

              {/* Pipeline Leads */}
              {allPipelineLeads.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      Pipeline Leads ({allPipelineLeads.length})
                    </h2>
                    <button
                      onClick={() => setViewMode(viewMode === 'list' ? 'kanban' : 'list')}
                      className="p-2 bg-gray-100 dark:bg-[#2A2A2A] hover:bg-gray-200 dark:hover:bg-[#333333] rounded-lg transition-colors"
                      title={`Switch to ${viewMode === 'list' ? 'kanban' : 'list'} view`}
                    >
                      {viewMode === 'list' ? (
                        <svg className="w-4 h-4 text-gray-600 dark:text-[#CCCCCC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h2a2 2 0 002-2z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-gray-600 dark:text-[#CCCCCC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                        </svg>
                      )}
                    </button>
                  </div>
                  
                  {viewMode === 'list' ? (
                    <div className="space-y-3">
                      {allPipelineLeads.map((request) => (
                        <IncomingRequestCard
                          key={request.id}
                          request={request}
                          onViewDetails={handleViewLeadDetails}
                          onStageChange={handleStageChange}
                          onDismiss={handleDismissRequest}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="border border-gray-200 dark:border-[#2A2A2A] rounded-2xl p-3 sm:p-4">
                      <div className="flex gap-3 overflow-x-auto pb-2">
                        {QUALIFICATION_STAGES.map((stage) => (
                          <QualificationStageColumn
                            key={stage.id}
                            stage={stage}
                            leads={qualifiedLeadsByStage[stage.id] || []}
                            onDrop={handleQualificationDrop}
                            onDragOver={(e) => e.preventDefault()}
                            onViewDetails={handleViewLeadDetails}
                            onConvertToProject={handleConvertToProject}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}


              {/* Active Projects */}
              {activeProjects.length > 0 && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                    Active Projects ({activeProjects.length})
                  </h2>
                  <div className="space-y-3">
                    {activeProjects.map((project) => (
                      <ProjectListItem
                        key={project.id}
                        project={project}
                        onClick={handleViewProjectDetails}
                        isActive={true}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Completed Projects */}
              {completedProjects.length > 0 && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                    Completed Projects ({completedProjects.length})
                  </h2>
                  <div className="space-y-3">
                    {completedProjects.map((project) => (
                      <ProjectListItem
                        key={project.id}
                        project={project}
                        onClick={handleViewProjectDetails}
                        isActive={false}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {allPipelineLeads.length === 0 && activeProjects.length === 0 && completedProjects.length === 0 && (
                <div className="border border-gray-200 dark:border-[#2A2A2A] rounded-2xl p-8 text-center">
                  <div className="w-16 h-16 border border-gray-200 dark:border-[#2A2A2A] rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Start Building Your Pipeline
                  </h3>
                  <p className="text-gray-500 dark:text-[#666666] mb-4">
                    Add your first lead to begin tracking your sales process
                  </p>
                  <button
                    onClick={() => setShowQuickAdd(true)}
                    className="px-6 py-3 bg-brand text-white font-semibold rounded-xl hover:bg-brand-hover transition-colors"
                  >
                    Add Your First Lead
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Lead Details Modal */}
      <AnimatePresence>
        {showLeadModal && (
          <LeadDetailsModal
            lead={selectedLead}
            isOpen={showLeadModal}
            onClose={() => {
              setShowLeadModal(false);
              setSelectedLead(null);
            }}
            onUpdateLead={handleUpdateLead}
            onConvertToProject={handleConvertToProject}
          />
        )}
      </AnimatePresence>

      {/* Quick Add Lead Form */}
      <AnimatePresence>
        <QuickAddLeadForm
          isOpen={showQuickAdd}
          onClose={() => setShowQuickAdd(false)}
          onAdd={handleAddLead}
        />
      </AnimatePresence>
    </div>
  );
}